import { SyndicationType } from '~/swagger/settings/Api';

export const settingsSyndicationTypeGroups: SyndicationType[][] = [
  [SyndicationType.Zillow, SyndicationType.Hotpads, SyndicationType.Trulia],
  [SyndicationType.Apartments],
  [SyndicationType.Rentmor],
];

export const listingSupportedSyndicationTypes: SyndicationType[] = settingsSyndicationTypeGroups.flat();

export const syndicationEntityLevels = ['Unit', 'UnitListing'] as const;

export type SyndicationEntityLevel = (typeof syndicationEntityLevels)[number];
export type SyndicationFixTarget = 'edit-unit' | 'edit-property' | 'edit-listing' | 'photos' | 'current-page' | 'listing-syndications';

export interface SyndicationErrorDetails {
  title: string;
  detail: string;
  field?: string;
  reason?: string;
  entityLevel?: SyndicationEntityLevel;
}

const SYNDICATION_FIELD_TARGET_MAP: Record<string, SyndicationFixTarget> = {
  Beds: 'edit-unit',
  Baths: 'edit-unit',
  UnitSizeSqft: 'edit-unit',
  unitNumber: 'edit-unit',

  Address: 'edit-property',
  Type: 'edit-property',

  RentalPrice: 'edit-listing',
  Title: 'edit-listing',

  Images: 'photos',

  Active: 'current-page',
  Listed: 'current-page',

  SyndicationSetting: 'listing-syndications',
};

const SYNDICATION_ENTITY_LEVEL_TARGET_MAP: Record<SyndicationEntityLevel, SyndicationFixTarget> = {
  Unit: 'edit-unit',
  UnitListing: 'edit-listing',
};

export function isSyndicationEntityLevel(value: unknown): value is SyndicationEntityLevel {
  return typeof value === 'string' && syndicationEntityLevels.includes(value as SyndicationEntityLevel);
}

function getSyndicationField(detail: string | undefined) {
  if (!detail) return undefined;

  const match = detail.match(/,\s*([^:,]+):/);
  if (match) {
    return match[1].trim();
  }

  return undefined;
}

export function getSyndicationFixTarget(
  error: Pick<SyndicationErrorDetails, 'detail' | 'entityLevel'> | string | null | undefined
): SyndicationFixTarget {
  const detail = typeof error === 'string' || error == null ? (error ?? undefined) : error.detail;
  const entityLevel = typeof error === 'string' || error == null ? undefined : error.entityLevel;

  if (entityLevel) {
    return SYNDICATION_ENTITY_LEVEL_TARGET_MAP[entityLevel];
  }

  const field = getSyndicationField(detail);
  if (field) {
    const target = SYNDICATION_FIELD_TARGET_MAP[field];
    if (target) return target;
  }

  return 'listing-syndications';
}

export interface SyndicationFixHrefParams {
  target: SyndicationFixTarget;
  portfolioId: string | undefined;
  propertyId: string | undefined;
  unitId: string | undefined;
  listingId: string | undefined;
}

export function buildSyndicationFixHref(params: SyndicationFixHrefParams): string | undefined {
  const { portfolioId, propertyId, unitId, listingId } = params;

  switch (params.target) {
    case 'edit-unit':
      if (portfolioId && listingId && unitId) {
        return `/listings/${portfolioId}/details/${listingId}/units/${unitId}/edit-unit`;
      }
      return undefined;
    case 'edit-property':
      if (portfolioId && propertyId) {
        return `/portfolios/${portfolioId}/properties/${propertyId}/edit`;
      }
      return undefined;
    case 'edit-listing':
      if (portfolioId && listingId) {
        return `/listings/${portfolioId}/details/${listingId}/edit-listing`;
      }
      return undefined;
    case 'photos':
      if (portfolioId && listingId) {
        return `/listings/${portfolioId}/details/${listingId}/photos`;
      }
      return undefined;
    case 'current-page':
      return undefined;
    case 'listing-syndications':
    default:
      if (portfolioId && listingId) {
        return `/listings/${portfolioId}/details/${listingId}/listing-syndications`;
      }
      return undefined;
  }
}
